import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { dsComposerApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { parseDsIndex, buildDsIndex, nextRev, FIRST_REV } from './dsIndexName';
import { Plus, FileDown, RefreshCw, Save, Copy, Rows3, LayoutList, Table, BookCheck, Library, BookOpen } from 'lucide-react';
import { sheetLayout, sheetLayouts } from './dsTemplateLayout';
import { SHARED_SHEET } from './pdf/dsGroupBySheet';
import { useDsTemplates } from './useDsTemplates';
import ActionMenu from '../../components/common/ActionMenu';
import exportDsPdf from './dsPdfExporter';
import { dsErrMsg, isTempId, LIGHT_FIELD, isSheetCopy, familyOf, revOf, familyLabel, byRevDesc, LEGACY_KEY, templateTypesOf, isSharedZone, storedZoneFor, onSheet, linesToBoxes, boxToLine, lineKey, COPY_ZONES, copyZones } from '../datasheets/dsBoxes';
import { PopulateModal, DSCanvas } from '../datasheets/DSCanvas';
import { IndexComposer } from '../datasheets/IndexComposer';
import { masterSource } from '../datasheets/masterSource';
import MasterCatalogTab from '../datasheets/MasterCatalogTab';
import DefinitionsTab from '../datasheets/DefinitionsTab';
import DefinitionModal from '../datasheets/DefinitionModal';
import '../../STUI.css';
import StuiSelect from '../../components/common/StuiSelect';
import { todayKey } from '../../utils/dateTime';

// Re-exported so the package preview keeps importing it from here.
export { DSPreview } from '../datasheets/DSPreview';

function TempTypeSelect({ value, onChange }) {
  const types = templateTypesOf(useDsTemplates());
  const options = value && !types.includes(value) ? [...types, value] : types;
  return (
    <StuiSelect className="stui-select" value={value} onChange={e => onChange(e.target.value)}>
      {options.map(t => <option key={t} value={t}>{t}</option>)}
    </StuiSelect>
  );
}

const DUP_MODES = [
  ['NewForm',   'New ISA Template',       'A new ISA Template (plain master form), copied from the source layout.'],
  ['SheetCopy', 'Index Sheet Datasheet', 'The source form for one Index Sheet. One per form and Index Sheet, shared by every project.'],
  ['Revision',  'Revision',       'The next revision of the source. The source stays as the previous revision.'],
];

function DuplicateDsModal({ allDsIndex, onClose, onCreate, onOpenExisting }) {
  const { currentUser } = useAuth();
  const auditCfg = { headers: { 'X-Changed-By': currentUser?.email || '' } };

  const [sourceId, setSourceId] = useState('');
  const [sourceLines, setSourceLines] = useState(null);   // null = not loaded yet
  const [loadingSrc, setLoadingSrc] = useState(false);
  const [selected, setSelected] = useState(COPY_ZONES.map(z => z.key));
  const [category, setCategory] = useState('');
  const [name,     setName]     = useState('');
  const [desc,     setDesc]     = useState('');
  const [tempType, setTempType] = useState('Instrument');
  const [mode,     setMode]     = useState('NewForm');
  const [indexSheet, setIndexSheet] = useState('');
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);
  const [existingId, setExistingId] = useState(null);

  const sources = [...allDsIndex].sort((a, b) =>
    `${a.ds_Category || ''}${a.ds_Name || ''}`.localeCompare(`${b.ds_Category || ''}${b.ds_Name || ''}`));

  // Load the source's rows so each zone can show a real count instead of a guess.
  useEffect(() => {
    if (!sourceId) { setSourceLines(null); return; }
    setLoadingSrc(true);
    dsComposerApi.getComposerLines(sourceId)
      .then(r => setSourceLines((r.data ?? []).map(l => l.ds_Line ?? 1)))
      .catch(() => setSourceLines([]))
      .finally(() => setLoadingSrc(false));
  }, [sourceId]);

  const specRows  = useDsTemplates();
  const srcRec    = allDsIndex.find(d => d.id === Number(sourceId)) ?? null;
  const zones     = useMemo(
    () => copyZones(specRows, srcRec?.ds_TempType, srcRec?.sheet_Count ?? 1, srcRec?.ds_Name),
    [specRows, srcRec?.ds_TempType, srcRec?.sheet_Count, srcRec?.ds_Name]
  );

  const countFor = zone =>
    sourceLines === null ? null : sourceLines.filter(n => zone.lines.includes(n)).length;

  const linesToCopy = zones
    .filter(z => selected.includes(z.key))
    .flatMap(z => z.lines);

  const totalRows = sourceLines === null ? null
    : sourceLines.filter(n => linesToCopy.includes(n)).length;

  const srcRecord = allDsIndex.find(d => d.id === Number(sourceId)) ?? null;

  // Prefill from the source so the common case is one field to change.
  useEffect(() => {
    if (!srcRecord) return;
    setCategory(srcRecord.ds_Category || '');
    setDesc(srcRecord.ds_Desc || '');
    setTempType(srcRecord.ds_TempType || 'Instrument');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId]);

  const toggle = key => setSelected(prev =>
    prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  const dateStr = todayKey().replace(/-/g, '');

  // What the new DS_Index will be - the same rules the server applies (DSController). New plain
  // forms keep the legacy key while any legacy row remains, until the one-time re-key.
  const legacy = allDsIndex.some(d => LEGACY_KEY.test(d.ds_Index || ''));
  let preview = null;
  let previewError = null;
  try {
    if (mode === 'NewForm' && name.trim())
      preview = legacy ? `${name.trim()}-${dateStr}XX` : buildDsIndex(name.trim(), '', FIRST_REV);
    if (mode === 'SheetCopy' && srcRec && indexSheet.trim())
      preview = buildDsIndex(srcRec.ds_Name || '', indexSheet.trim(), FIRST_REV);
    if (mode === 'Revision' && srcRec) {
      const p = parseDsIndex(srcRec.ds_Index);
      if (p) {
        const top = allDsIndex.filter(d => familyOf(d) === p.family).map(revOf).sort().pop() || p.rev;
        preview = `${p.family}_${nextRev(top)}`;
      } else {
        preview = `${srcRec.ds_Name}-${dateStr}XX`;
      }
    }
  } catch (e) { previewError = e.message; }

  const needs = mode === 'NewForm' ? !!name.trim() : mode === 'SheetCopy' ? !!indexSheet.trim() : true;

  const handleDuplicate = async () => {
    if (!sourceId || !needs) return;
    setSaving(true);
    setError(null);
    setExistingId(null);
    try {
      const res = await dsComposerApi.duplicateIndex(sourceId, {
        mode,
        category: category.trim() || null,
        name:     mode === 'NewForm' ? name.trim() : null,
        indexSheet: mode === 'SheetCopy' ? indexSheet.trim() : null,
        desc:     desc.trim() || null,
        tempType: tempType || 'Instrument',
        // Everything selected still sends explicit lines; the server treats an
        // empty array as "copy all", which is not what a cleared list means.
        lines: linesToCopy,
      }, auditCfg);
      onCreate(res.data);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to duplicate datasheet.');
      setExistingId(e?.response?.data?.existingId ?? null);
      setSaving(false);
    }
  };

  const canGo = !!sourceId && needs && !previewError && selected.length > 0 && !saving;

  // The overlay deliberately has no onClick dismiss. A stray click - or merely releasing
  // the mouse off a select or datalist after using it - delivered a click to the overlay
  // and threw away everything typed. Closing is explicit: the header X or Cancel.
  return (
    <div className="stui-modal-overlay">
      <div className="stui-modal stui-modal-md" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, width: '92vw' }}>
        <div className="stui-modal-header">
          <span className="stui-modal-title">Duplicate Datasheet</span>
          <button className="stui-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="stui-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && (
            <div className="stui-alert stui-alert-error">
              {error}
              {existingId && (
                <div className="stui-btn-row">
                  <button className="stui-btn stui-btn-secondary stui-btn-sm"
                    onClick={() => { onOpenExisting?.(existingId); onClose(); }}>Open the existing one</button>
                </div>
              )}
            </div>
          )}

          <div className="stui-field-group">
            <label className="stui-field-label">Create</label>
            <div className="stui-segmented">
              {DUP_MODES.map(([v, l]) => (
                <button key={v} className={`stui-segmented-btn${mode === v ? ' selected' : ''}`}
                  onClick={() => { setMode(v); setError(null); setExistingId(null); }}>{l}</button>
              ))}
            </div>
            <p className="stui-hint">{DUP_MODES.find(([v]) => v === mode)[2]}</p>
          </div>

          <div className="stui-field-group">
            <label className="stui-field-label">Copy from</label>
            <StuiSelect className="stui-select" value={sourceId} onChange={e => setSourceId(e.target.value)}>
              <option value="">— select a template —</option>
              {sources.map(d => (
                <option key={d.id} value={d.id}>
                  {d.ds_Category ? `${d.ds_Category} / ` : ''}{d.ds_Name} — {d.revision || d.ds_Index}
                  {d.ds_TempType ? ` (${d.ds_TempType})` : ''}
                </option>
              ))}
            </StuiSelect>
          </div>

          {sourceId && (
            <div className="stui-field-group">
              <label className="stui-label">What to copy</label>
              <div className="stui-checkbox-list">
                {zones.map(z => {
                  const n = countFor(z);
                  return (
                    <label key={z.key} className="stui-checkbox-list-item">
                      <input type="checkbox" className="stui-checkbox-input" checked={selected.includes(z.key)} onChange={() => toggle(z.key)} />
                      <span className="stui-checkbox-list-item-name">{z.label}</span>
                      <span className="stui-checkbox-list-item-count">
                        {loadingSrc ? '…' : n === null ? '' : `${n} row${n !== 1 ? 's' : ''}`}
                      </span>
                    </label>
                  );
                })}
              </div>
              <div className="stui-btn-row">
                <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setSelected(COPY_ZONES.map(z => z.key))}>Select All</button>
                <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setSelected([])}>Clear</button>
              </div>
              {totalRows !== null && (
                <p className="stui-hint">{totalRows} row{totalRows !== 1 ? 's' : ''} will be copied into a new template</p>
              )}
            </div>
          )}

          <div className="stui-field-group">
            <label className="stui-field-label">DS_Category <span className="stui-label-xs-muted">(optional)</span></label>
            <input className="stui-field-input" style={LIGHT_FIELD} list="dup-category-list" value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. 20F - Flow" />
            <datalist id="dup-category-list">
              {[...new Set(allDsIndex.map(d => d.ds_Category).filter(Boolean))].sort().map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
          {mode === 'NewForm' && (
            <div className="stui-field-group">
              <label className="stui-field-label">DS_Name</label>
              <input className="stui-field-input" style={LIGHT_FIELD} value={name} onChange={e => setName(e.target.value)} placeholder="new form number, e.g. 20F2001" />
            </div>
          )}
          {mode === 'SheetCopy' && (
            <div className="stui-field-group">
              <label className="stui-field-label">Index Sheet</label>
              <input className="stui-field-input" style={LIGHT_FIELD} value={indexSheet} onChange={e => setIndexSheet(e.target.value)} placeholder="e.g. CV-BRY-005-001-FL" />
            </div>
          )}
          <div className="stui-field-group">
            <label className="stui-field-label">DS_Desc <span className="stui-label-xs-muted">(optional)</span></label>
            <input className="stui-field-input" style={LIGHT_FIELD} value={desc} onChange={e => setDesc(e.target.value)} />
          </div>
          <div className="stui-field-group">
            <label className="stui-field-label">DS_TempType</label>
            <TempTypeSelect value={tempType} onChange={setTempType} />
          </div>
          {previewError && <div className="stui-alert stui-alert-error">{previewError}</div>}
          {preview && !previewError && (
            <div className="stui-label-xs-muted">ID will be: {preview} — created unpublished</div>
          )}
        </div>
        <div className="stui-modal-footer">
          <button className="stui-btn stui-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="stui-btn stui-btn-primary" onClick={handleDuplicate} disabled={!canGo}>
            {saving ? 'Duplicating…'
              : totalRows ? `Duplicate ${totalRows} Row${totalRows !== 1 ? 's' : ''}`
              : 'Duplicate'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewDsModal({ onClose, onCreate }) {
  const { currentUser } = useAuth();
  const auditCfg = { headers: { 'X-Changed-By': currentUser?.email || '' } };
  const [category,   setCategory]   = useState('');
  const [tempType,   setTempType]   = useState('Instrument');
  // Total pages INCLUDING Application Specific, which is on every datasheet and always last.
  // 2 is the floor for anything created now: one content page plus App Specific. A valve is 3.
  const [sheetCount, setSheetCount] = useState(2);
  const [name,       setName]       = useState('');
  const [desc,       setDesc]       = useState('');
  const [categories, setCategories] = useState([]);
  const [legacy,     setLegacy]     = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);

  useEffect(() => {
    dsComposerApi.getIndex()
      .then(res => {
        const rows = res.data || [];
        setCategories([...new Set(rows.map(r => r.ds_Category).filter(Boolean))].sort());
        setLegacy(rows.some(r => LEGACY_KEY.test(r.ds_Index || '')));
      })
      .catch(() => {});
  }, []);

  const handleCreate = async () => {
    if (!name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError(null);
    try {
      const res = await dsComposerApi.createIndex({
        category: category.trim() || null,
        name:     name.trim(),
        desc:     desc.trim() || null,
        tempType: tempType || 'Instrument',
        sheetCount,
      }, auditCfg);
      onCreate(res.data); onClose();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to create datasheet.');
      setSaving(false);
    }
  };

  const dateStr = todayKey().replace(/-/g, '');

  // The DS_Index the server will assign (DSController): the legacy key while any legacy row
  // remains, otherwise {name}_ISA-MFG-GRP-NUM-FP_00.
  let preview = null;
  let previewError = null;
  if (name.trim()) {
    try { preview = legacy ? `${name.trim()}-${dateStr}XX` : buildDsIndex(name.trim(), '', FIRST_REV); }
    catch (e) { previewError = e.message; }
  }

  // The overlay deliberately has no onClick dismiss. A stray click - or merely releasing
  // the mouse off a select or datalist after using it - delivered a click to the overlay
  // and threw away everything typed. Closing is explicit: the header X or Cancel.
  return (
    <div className="stui-modal-overlay">
      <div className="stui-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460, width: '92vw' }}>
        <div className="stui-modal-header">
          <span className="stui-modal-title">New Datasheet</span>
          <button className="stui-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="stui-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && <div className="stui-alert stui-alert-error">{error}</div>}
          <div className="stui-field-group">
            <label className="stui-field-label">DS_Category <span className="stui-label-xs-muted">(optional)</span></label>
            <input
              className="stui-field-input" style={LIGHT_FIELD}
              list="ds-category-list"
              value={category}
              onChange={e => setCategory(e.target.value)}
              placeholder="e.g. 20F - Flow"
            />
            <datalist id="ds-category-list">
              {categories.map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div className="stui-field-group">
            <label className="stui-field-label">DS_Name</label>
            <input className="stui-field-input" style={LIGHT_FIELD} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. 20F2001" autoFocus />
          </div>
          <div className="stui-field-group">
            <label className="stui-field-label">DS_TempType</label>
            {/* The types are the layout spec's own (UtlCfg_DSTemplate), so a new template type
                is a spec row rather than a code change. */}
            <TempTypeSelect value={tempType} onChange={setTempType} />
          </div>
          <div className="stui-field-group">
            <label className="stui-field-label">Sheets</label>
            {/* A property of the template, not something added later in the composer - a valve
                simply has three pages. The last one is always Application Specific, so this
                counts it: 2 means one content page plus App Specific. */}
            <StuiSelect className="stui-select" value={sheetCount}
              onChange={e => setSheetCount(parseInt(e.target.value, 10))}>
              {[2, 3, 4, 5, 6].map(n => (
                <option key={n} value={n}>
                  {n} ({n - 1} content + Application Specific)
                </option>
              ))}
            </StuiSelect>
          </div>
          <div className="stui-field-group">
            <label className="stui-field-label">DS_Desc <span className="stui-label-xs-muted">(optional)</span></label>
            <input className="stui-field-input" style={LIGHT_FIELD} value={desc} onChange={e => setDesc(e.target.value)} placeholder="e.g. Flow transmitter standard form" />
          </div>
          {previewError && <div className="stui-alert stui-alert-error">{previewError}</div>}
          {preview && !previewError && (
            <div className="stui-label-xs-muted">ID will be: {preview}</div>
          )}
        </div>
        <div className="stui-modal-footer">
          <button className="stui-btn stui-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="stui-btn stui-btn-primary" onClick={handleCreate} disabled={saving}>{saving ? 'Creating…' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}

// The Index Composer and Catalog tabs edit the master catalog, an add-on to this page's own key.
const MASTER_CATALOG_KEY = 'utilities.ds.master.catalog';

export default function DSComposerPage() {
  const { notify } = useNotifications();
  const { currentUser, hasPermission } = useAuth();
  const canCatalog = hasPermission(MASTER_CATALOG_KEY, 'global');
  const auditCfg = { headers: { 'X-Changed-By': currentUser?.email || '' } };
  // Real DB id -> lineKey as last read from the server. The save diff is taken
  // against this, so an untouched row is never rewritten and keeps its own
  // DS_RevUser/DS_RevDate.
  const baselineRef = useRef(new Map());
  const [allDsIndex,       setAllDsIndex]       = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDsName,   setSelectedDsName]   = useState(null);
  const [selectedDsId,     setSelectedDsId]     = useState(null);
  const [boxes,        setBoxesRaw]     = useState([]);
  const [loadingIndex, setLoadingIndex] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [publishing,   setPublishing]   = useState(false);
  const [hasChanges,   setHasChanges]   = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showDupModal, setShowDupModal] = useState(false);
  const [showPopulate, setShowPopulate] = useState(false);
  const [activeTab,    setActiveTab]    = useState('template');
  const [definitionLabel, setDefinitionLabel] = useState(null);   // label clicked in the Index Composer

  const setBoxes = useCallback(updater => { setBoxesRaw(updater); setHasChanges(true); }, []);

  const loadIndex = useCallback(async (type) => {
    setLoadingIndex(true);
    setSelectedDsId(null);
    setSelectedDsName(null);
    setBoxesRaw([]);
    setHasChanges(false);
    try {
      const res = await dsComposerApi.getIndex(type || undefined);
      const records = res.data ?? [];
      setAllDsIndex(records);
      const cats = [...new Set(records.map(d => d.ds_Category).filter(Boolean))].sort();
      if (cats.length > 0) setSelectedCategory(cats[0]);
      return records;
    } catch (e) { notify.error(dsErrMsg(e, 'Failed to load datasheets.')); return []; }
    finally { setLoadingIndex(false); }
  }, [notify]);

  // loadIndex still takes a type - the API filter is real and the sync utility and
  // package generation both use it. The header just no longer exposes a picker for it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadIndex(); }, []);

  // Which templates the selectors list: ISA Templates (plain master forms, the default), Index
  // Sheet Datasheets (per-Index-Sheet copies), or both. Copies far outnumber the templates.
  const [listFilter, setListFilter] = useState('plain');
  const visibleDs = allDsIndex.filter(d => listFilter === 'all' || (listFilter === 'sheet') === isSheetCopy(d));

  // Derived lists. The Datasheet select lists FAMILIES (form + Index Sheet), not names: every
  // per-sheet copy of 20C2011 shares the DS_Name 20C2011. selectedDsName holds the family key.
  const categories = [...new Set(visibleDs.map(d => d.ds_Category).filter(Boolean))].sort();
  const familiesInCat = [...new Map(visibleDs
    .filter(d => d.ds_Category === selectedCategory)
    .map(d => [familyOf(d), d])).entries()]
    .sort(([a], [b]) => a.localeCompare(b));
  const dsNamesInCat = familiesInCat.map(([f]) => f);
  const revisionsForName = allDsIndex
    .filter(d => d.ds_Category === selectedCategory && familyOf(d) === selectedDsName)
    .sort(byRevDesc);

  const selectedRecord = allDsIndex.find(d => d.id === selectedDsId) ?? null;
  const isPublished    = !!selectedRecord?.published;
  // Index Sheet Datasheets are edited and revised in the Project Datasheet Composer; here they only show.
  const isCopyView     = !!selectedRecord && isSheetCopy(selectedRecord);
  const isLocked       = isPublished || isCopyView;

  // ── Multi-sheet ────────────────────────────────────────────────────────────
  //
  // A template can span pages. DS_Sheet partitions the boxes; DS_Line keeps its
  // existing zone meanings unchanged within each sheet.
  //
  // DSCanvas owns the whole list it is handed and replaces it wholesale, so it is
  // given ONLY the active sheet's boxes and a setter that stitches the other sheets
  // back around the result. Handing it every box and filtering inside the canvas
  // would mean teaching every drag, drop and rebalance path about sheets.
  const [activeSheet, setActiveSheet] = useState(1);

  // Sheets are a property of the TEMPLATE, not something anyone adds ad hoc: a valve simply
  // has three pages and the composer navigates them. Sheet_Count is that page count, set from
  // the datasheet type when the template is created.
  //
  // Application Specific is on every datasheet and is always LAST, so it needs no column of
  // its own - it is sheet number Sheet_Count, whatever that is.
  const sheetCount = Math.max(1, selectedRecord?.sheet_Count ?? 1);

  const sheetNumbers = useMemo(
    () => Array.from({ length: sheetCount }, (_, i) => i + 1),
    [sheetCount]
  );

  const sheetLabel = useCallback(
    // The last page is Application Specific by definition. A one-page template has no separate
    // App Specific sheet yet, so sheet 1 is just sheet 1 - do not label it as something it is not.
    n => (sheetCount > 1 && n === sheetCount ? 'Application Specific' : `Content ${n}`),
    [sheetCount]
  );

  // Changing template resets the page, and a template with fewer pages must not leave the
  // canvas pointed at one that does not exist.
  useEffect(() => { setActiveSheet(1); }, [selectedDsId]);

  useEffect(() => {
    if (activeSheet > sheetCount) setActiveSheet(1);
  }, [sheetCount, activeSheet]);

  const sheetBoxes = useMemo(
    () => boxes.filter(b => onSheet(b, activeSheet)),
    [boxes, activeSheet]
  );

  const specRows = useDsTemplates();
  const tempType = selectedRecord?.ds_TempType ?? null;
  const formName = selectedRecord?.ds_Name ?? null;
  const layout   = useMemo(
    () => sheetLayout(specRows, tempType, activeSheet, sheetCount, formName),
    [specRows, tempType, activeSheet, sheetCount, formName]
  );

  // DSCanvas replaces the whole list it is handed, so it gets only the active sheet and a
  // setter that stitches the other sheets back around the result. Handing it every box would
  // mean teaching every drag, drop and rebalance path about sheets.
  const setSheetBoxes = useCallback(updater => {
    setBoxes(prev => {
      const mine   = prev.filter(b => onSheet(b, activeSheet));
      const others = prev.filter(b => !onSheet(b, activeSheet));
      const next   = typeof updater === 'function' ? updater(mine) : updater;
      // A header or center box is shared, so an edit on any sheet edits the one copy. Anything
      // else belongs to the page it was dropped on - including a box dragged out of the header.
      const placed = b => {
        const sheet  = isSharedZone(b.zone) ? SHARED_SHEET
          : (b.sheet == null || b.sheet === SHARED_SHEET) ? activeSheet : b.sheet;
        const dsZone = storedZoneFor(b, layout);
        return sheet === b.sheet && dsZone === b.dsZone ? b : { ...b, sheet, dsZone };
      };
      return [...others, ...next.map(placed)];
    });
  }, [setBoxes, activeSheet, layout]);

  // A filter change can hide the open category or datasheet; fall back to the first visible one.
  useEffect(() => {
    if (!categories.includes(selectedCategory)) {
      setSelectedCategory(categories[0] ?? null);
      setSelectedDsName(null);
      setSelectedDsId(null);
    } else if (!dsNamesInCat.includes(selectedDsName)) {
      setSelectedDsName(dsNamesInCat[0] ?? null);
      setSelectedDsId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listFilter]);

  // Auto-select first DS_Name when category changes
  useEffect(() => {
    if (dsNamesInCat.length > 0 && !dsNamesInCat.includes(selectedDsName)) {
      setSelectedDsName(dsNamesInCat[0]);
      setSelectedDsId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  // Auto-select first revision when name changes
  useEffect(() => {
    if (revisionsForName.length > 0 && !revisionsForName.find(d => d.id === selectedDsId)) {
      setSelectedDsId(revisionsForName[0].id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDsName]);

  const loadLines = useCallback(async (id) => {
    if (!id) return;
    setLoadingLines(true);
    setHasChanges(false);
    try {
      const res = await dsComposerApi.getComposerLines(id);
      const loaded = linesToBoxes(res.data ?? []);
      baselineRef.current = new Map(loaded.map(b => [b.id, lineKey(boxToLine(b, id))]));
      setBoxesRaw(loaded);
    } catch (e) { notify.error(dsErrMsg(e, 'Failed to load layout.')); }
    finally { setLoadingLines(false); }
  }, [notify]);

  useEffect(() => { loadLines(selectedDsId); }, [selectedDsId, loadLines]);

  // Opens a template just created (or found) by id, switching the filter so it is visible.
  const openTemplate = useCallback(async id => {
    const records = await loadIndex();
    const rec = records.find(d => d.id === id);
    if (!rec) return;
    setListFilter(isSheetCopy(rec) ? 'sheet' : 'plain');
    setSelectedCategory(rec.ds_Category ?? null);
    setSelectedDsName(familyOf(rec));
    setSelectedDsId(rec.id);
  }, [loadIndex]);

  const handleNewCreated = data => {
    notify.success(`Created ${data.dsIndex}`);
    openTemplate(data.id);
  };

  // Diff against the baseline captured at load: only touched rows are written.
  // The old implementation deleted and re-inserted every row on every save,
  // which discarded all row identities and re-stamped every DS_RevUser.
  const handleDuplicated = data => {
    notify.success(`Created ${data.dsIndex} — ${data.copiedLines} row${data.copiedLines !== 1 ? 's' : ''} copied`);
    openTemplate(data.id);
  };

  const handleSave = async () => {
    if (!selectedDsId || isLocked) return;
    setSaving(true);
    try {
      const baseline = baselineRef.current;
      const creates = [];
      const updates = [];
      const kept    = new Set();

      for (const box of boxes) {
        const payload = boxToLine(box, selectedDsId);
        if (isTempId(box.id)) { creates.push(payload); continue; }
        kept.add(box.id);
        if (baseline.get(box.id) !== lineKey(payload)) updates.push({ id: box.id, payload });
      }
      const deletes = [...baseline.keys()].filter(id => !kept.has(id));

      if (!creates.length && !updates.length && !deletes.length) {
        setHasChanges(false);
        notify.success('No changes to save.');
        return;
      }

      // Deletes before creates so a freed DS_Line/DS_RowOrder is available again.
      for (const { id, payload } of updates) await dsComposerApi.updateConfigLine(id, payload, auditCfg);
      for (const id of deletes)              await dsComposerApi.deleteConfigLine(id, auditCfg);
      for (const payload of creates)         await dsComposerApi.createConfigLine(payload, auditCfg);

      // Re-read so created rows pick up their real ids and the baseline resets;
      // this is what makes an immediate second save a genuine no-op.
      await loadLines(selectedDsId);
      notify.success(`Saved — ${creates.length} added, ${updates.length} changed, ${deletes.length} removed.`);
    } catch (err) {
      const detail = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'unknown';
      notify.error(`Save failed: ${detail}`);
    } finally { setSaving(false); }
  };

  const handleTogglePublished = async () => {
    if (!selectedDsId) return;
    const nextPub = !isPublished;
    setPublishing(true);
    try {
      await dsComposerApi.setPublished(selectedDsId, nextPub, auditCfg);
      setAllDsIndex(prev => prev.map(d => d.id === selectedDsId ? { ...d, published: nextPub } : d));
      notify.success(nextPub ? 'Template published — now read-only.' : 'Template unpublished — editing enabled.');
    } catch (err) {
      notify.error(`Failed: ${err?.response?.data?.error || err?.message}`);
    } finally { setPublishing(false); }
  };

  const [exporting, setExporting] = useState(false);

  const handleExportTemplate = async () => {
    if (!selectedDsId || boxes.length === 0) return;
    setExporting(true);
    try {
      const dsName = selectedRecord?.ds_Index || String(selectedDsId);
      await exportDsPdf({ boxes, resolvedValues: {}, userValues: {}, metadata: { dsName, sheetLayouts: sheetLayouts(specRows, tempType, sheetCount, formName) } });
    } catch (err) {
      notify.error(`Export failed: ${err.message}`);
    } finally { setExporting(false); }
  };

  // 6-dot contents. `hidden` rather than `disabled` throughout: an action you cannot
  // take right now simply is not listed. Note Save disappears once there is nothing
  // to save, which is also the only remaining signal that changes are pending.
  const templateActions = [
    { key: 'export', icon: <FileDown size={16} />, label: exporting ? 'Exporting…' : 'Download PDF',
      hidden: !selectedDsId || boxes.length === 0,
      onClick: handleExportTemplate },
    { key: 'populate', icon: <Rows3 size={16} />, label: 'Populate Rows…',
      hidden: isLocked || !selectedDsId,
      onClick: () => setShowPopulate(true) },
    { key: 'new', icon: <Plus size={16} />, label: 'New Blank…',
      onClick: () => setShowNewModal(true) },
    { key: 'duplicate', icon: <Copy size={16} />, label: 'Duplicate from Existing…',
      onClick: () => setShowDupModal(true) },
    { key: 'save', icon: <Save size={16} />, label: saving ? 'Saving…' : 'Save',
      hidden: isLocked || !selectedDsId || !hasChanges,
      onClick: handleSave },
    { key: 'publish', icon: <BookCheck size={16} />,
      label: publishing ? '…' : 'Publish',
      title: 'Publish — locks template',
      // Publishing freezes the template, so it is offered only against saved work.
      hidden: !selectedDsId || hasChanges || isLocked,
      onClick: handleTogglePublished },
    { key: 'danger-divider', divider: 'strong' },
    { key: 'unpublish', icon: <BookCheck size={16} />,
      label: publishing ? '…' : 'Unpublish',
      title: 'Unpublish to allow edits',
      danger: true,
      // Publishing freezes the template, so it is offered only against saved work.
      hidden: !selectedDsId || hasChanges || !isPublished || isCopyView,
      onClick: handleTogglePublished },
  ].filter(a => !a.hidden);

  return (
    <div className="stui-module-container">

      {/* ── Module header: title and tabs only ── */}
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <h1>Master Datasheet Composer</h1>
          {/* Tabs belong in the header: the active chip's margin-bottom:-1px
              overlaps the header's bottom border so it merges into the panel. */}
          <div className="stui-module-header-tabs">
            <button className={`stui-dataentry-tab${activeTab === 'template' ? ' active' : ''}`}
              onClick={() => setActiveTab('template')}>
              <LayoutList size={14} /><span>Template Composer</span>
            </button>
            {canCatalog && (
              <>
                <button className={`stui-dataentry-tab${activeTab === 'index' ? ' active' : ''}`}
                  onClick={() => setActiveTab('index')}>
                  <Table size={14} /><span>Index Composer</span>
                </button>
                <button className={`stui-dataentry-tab${activeTab === 'catalog' ? ' active' : ''}`}
                  onClick={() => setActiveTab('catalog')}>
                  <Library size={14} /><span>Catalog</span>
                </button>
              </>
            )}
            <button className={`stui-dataentry-tab${activeTab === 'definitions' ? ' active' : ''}`}
              onClick={() => setActiveTab('definitions')}>
              <BookOpen size={14} /><span>Definitions</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Template selector bar. Its own band below the tabs, matching the
             Index Composer: labelled selects on the left, actions pushed right. ── */}
      {activeTab === 'template' && (
        <div className="stui-page-header" style={{ gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
          <label className="stui-label" style={{ minWidth: 'unset' }}>Show</label>
          <StuiSelect className="stui-select" value={listFilter} onChange={e => setListFilter(e.target.value)} disabled={loadingIndex}>
            <option value="plain">ISA Templates</option>
            <option value="sheet">Index Sheet Datasheets</option>
            <option value="all">All</option>
          </StuiSelect>

          <label className="stui-label" style={{ minWidth: 'unset' }}>Category</label>
          <StuiSelect className="stui-select" value={selectedCategory ?? ''} onChange={e => { setSelectedCategory(e.target.value || null); setSelectedDsName(null); setSelectedDsId(null); }} disabled={loadingIndex} style={{ minWidth: 140 }}>
            {loadingIndex ? <option>Loading…</option> : categories.map(c => <option key={c} value={c}>{c}</option>)}
          </StuiSelect>

          <label className="stui-label" style={{ minWidth: 'unset' }}>Datasheet</label>
          <StuiSelect className="stui-select" value={selectedDsName ?? ''} onChange={e => { setSelectedDsName(e.target.value || null); setSelectedDsId(null); }} disabled={loadingIndex || dsNamesInCat.length === 0} style={{ minWidth: 240 }}>
            {dsNamesInCat.length === 0 ? <option>No templates</option>
              : familiesInCat.map(([f, d]) => (
                  <option key={f} value={f}>{familyLabel(d)}{d.ds_Desc ? ` — ${d.ds_Desc}` : ''}</option>
                ))}
          </StuiSelect>

          <label className="stui-label" style={{ minWidth: 'unset' }}>Revision</label>
          <StuiSelect className="stui-select" value={selectedDsId ?? ''} onChange={e => setSelectedDsId(parseInt(e.target.value, 10) || null)} disabled={loadingIndex || revisionsForName.length === 0} style={{ minWidth: 140 }}>
            {revisionsForName.length === 0 ? <option>No revisions</option>
              : revisionsForName.map(d => <option key={d.id} value={d.id}>{revOf(d)}</option>)}
          </StuiSelect>

          {selectedRecord && (
            <span className={isPublished ? 'stui-badge stui-badge-accepted' : 'stui-badge stui-badge-expired'}>
              {isPublished ? 'Published' : 'Draft'}
            </span>
          )}

          {/* Sheet strip. Only shown once a template is open - on an empty page it would be
              a control for nothing. Reuses the existing data-entry tab chips rather than
              introducing a second tab look. */}
          {selectedDsId && sheetCount > 1 && (
            <div className="stui-module-header-tabs">
              {sheetNumbers.map(n => (
                <button
                  key={n}
                  className={`stui-dataentry-tab${activeSheet === n ? ' active' : ''}`}
                  onClick={() => setActiveSheet(n)}
                  title={sheetLabel(n)}
                >
                  <span>{sheetLabel(n)}</span>
                </button>
              ))}
            </div>
          )}


          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="stui-btn stui-btn-secondary" onClick={() => loadLines(selectedDsId)} disabled={!selectedDsId || loadingLines} title="Refresh"><RefreshCw size={14} /></button>
            {/* Unavailable actions are hidden rather than disabled - the menu shows
                only what you can actually do right now. */}
            <ActionMenu disabled={!templateActions.some(a => !a.divider)} items={templateActions} />
          </div>
        </div>
      )}

      {/* ── Tab content ── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {activeTab === 'template' && (
          <div style={{ flex: 1, overflow: 'hidden', padding: '12px 20px', position: 'relative' }}>
            {!selectedDsId ? (
              <div className="stui-detail-empty">Select a datasheet above or create a new one.</div>
            ) : loadingLines ? (
              <div className="stui-detail-empty">Loading…</div>
            ) : (
              <>
                <DSCanvas boxes={sheetBoxes} setBoxes={setSheetBoxes} sheetCount={sheetCount} layout={layout} readOnly={isLocked} allBoxes={boxes} />
                {isLocked && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 32, pointerEvents: 'none', zIndex: 10 }}>
                    <div style={{ background: 'var(--color-success)', color: 'var(--color-on-success)', padding: '6px 18px', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 13, boxShadow: 'var(--shadow-md)' }}>
                      {isCopyView
                        ? 'Index Sheet Datasheet — view only. Edit or revise it in the Project Datasheet Composer.'
                        : 'Published — Read Only. Unpublish to make changes.'}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {/* Master values for catalog Index Sheets. */}
        {canCatalog && activeTab === 'index' && (
          <IndexComposer allDsIndex={allDsIndex} source={masterSource} onLabelClick={setDefinitionLabel} />
        )}
        {canCatalog && activeTab === 'catalog' && (
          <MasterCatalogTab allDsIndex={allDsIndex} />
        )}
        {activeTab === 'definitions' && <DefinitionsTab scope="master" />}
      </div>

      {showNewModal && <NewDsModal onClose={() => setShowNewModal(false)} onCreate={handleNewCreated} />}
      {showDupModal && <DuplicateDsModal allDsIndex={allDsIndex} onClose={() => setShowDupModal(false)} onCreate={handleDuplicated} onOpenExisting={openTemplate} />}
      {definitionLabel && <DefinitionModal label={definitionLabel} scope="master" onClose={() => setDefinitionLabel(null)} />}
      {showPopulate && <PopulateModal boxes={sheetBoxes} setBoxes={setSheetBoxes} layout={layout} notify={notify} onClose={() => setShowPopulate(false)} />}
    </div>
  );
}
